import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Fade,
  InputAdornment,
  Menu,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@api/api";

import SearchIcon from "@mui/icons-material/Search";
import AccountBalanceRoundedIcon from "@mui/icons-material/AccountBalanceRounded";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import InfoIcon from "@mui/icons-material/Info";
import CheckIcon from "@mui/icons-material/Check";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SettingsIcon from "@mui/icons-material/Settings";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";

import PaginacaoLista from "../../../componentes/comuns/PaginacaoLista";
import {
  coresTextosGlobais, iconesGlobais,
  tamanhosControlesGlobais,
  tamanhosPaginasAdministrativas,
} from "../../../estilos/global";

const OPCOES_ITENS_POR_PAGINA = [5, 10, 20];

const COLUNAS_ORDENAVEIS = [
  {
    chave: "nome",
    titulo: "Mantenedora",
    largura: "40%",
    Icone: AccountBalanceRoundedIcon,
  },
  {
    chave: "cidade",
    titulo: "Cidade",
    largura: "20%",
    Icone: LocationOnIcon,
  },
  {
    chave: "email",
    titulo: "E-mail",
    largura: "25%",
    Icone: EmailIcon,
  },
];

const normalizeText = (value = "") =>
  String(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("pt-BR")
    .trim();

const compareText = (firstValue = "", secondValue = "") =>
  firstValue.localeCompare(secondValue, "pt-BR", {
    sensitivity: "base",
    numeric: true,
  });

// ======================================================
// TABELA DE MANTENEDORAS
// ======================================================

const MaintainerTable = ({ maintainers, onProfileClick, ordem, onSort }) => (
  <TableContainer
    component={Paper}
    elevation={0}
    sx={{
      border: "1px solid var(--sear-borda, #d7e7f7)",
      borderRadius: 1.5,
      overflowX: "auto",
    }}
  >
    <Table
      aria-label="Lista de mantenedoras"
      size="small"
      sx={{
        minWidth: 850,
        tableLayout: "fixed",
        "& .MuiTableCell-root": {
          borderColor: "var(--sear-borda, #dfe9f3)",
        },
      }}
    >
      <TableHead sx={{ backgroundColor: "var(--sear-superficie-azul, #edf6fe)" }}>
        <TableRow>
          {COLUNAS_ORDENAVEIS.map(({ chave, titulo, largura, Icone }) => {
            const ativa = ordem.coluna === chave;

            return (
              <TableCell
                key={chave}
                sortDirection={ativa ? ordem.direcao : false}
                sx={{
                  width: largura,
                  px: 1.4,
                  py: 0.55,
                  borderRight: "1px solid var(--sear-borda, #d9e7f3)",
                }}
              >
                <TableSortLabel
                  active={ativa}
                  direction={ativa ? ordem.direcao : "asc"}
                  onClick={() => onSort(chave)}
                  IconComponent={
                    ativa
                      ? ordem.direcao === "asc"
                        ? ArrowUpwardIcon
                        : ArrowDownwardIcon
                      : UnfoldMoreIcon
                  }
                  sx={{
                    color: coresTextosGlobais.cabecalhoTabela,
                    fontSize: tamanhosPaginasAdministrativas.cabecalhoTabela,
                    fontWeight: 700,
                    "&:hover": {
                      color: "var(--sear-azul, #0767d8)",
                    },
                    "&.Mui-active": {
                      color: coresTextosGlobais.cabecalhoTabela,
                    },
                    "& .MuiTableSortLabel-icon": {
                      opacity: 1,
                      color: "var(--sear-azul, #385b90)",
                      fontSize: iconesGlobais.conteudoTabela.fontSize,
                      ml: 0.35,
                      transform: "none !important",
                    },
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.65,
                    }}
                  >
                    <Icone
                      sx={{
                        color: "var(--sear-azul, #087cf0)",
                        fontSize: iconesGlobais.tabela.fontSize,
                        flexShrink: 0,
                      }}
                    />

                    <Box component="span" sx={{ whiteSpace: "nowrap" }}>
                      {titulo}
                    </Box>
                  </Box>
                </TableSortLabel>
              </TableCell>
            );
          })}

          <TableCell
            align="center"
            sx={{
              width: "15%",
              px: 1,
              py: 0.55,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.65,
                color: "var(--sear-azul, #27456f)",
              }}
            >
              <SettingsIcon
                sx={{
                  color: "var(--sear-azul, #087cf0)",
                  fontSize: iconesGlobais.tabela.fontSize,
                }}
              />

              <Typography
                component="span"
                sx={{
                  color: coresTextosGlobais.cabecalhoTabela,
                  fontSize: tamanhosPaginasAdministrativas.cabecalhoTabela,
                  fontWeight: 700,
                }}
              >
                Ações
              </Typography>
            </Box>
          </TableCell>
        </TableRow>
      </TableHead>

      <TableBody>
        {maintainers.map((maintainer) => (
          <TableRow
            key={maintainer.id}
            hover
            sx={{
              height: 38,
              "&:hover": {
                backgroundColor: "var(--sear-superficie, #fbfdff)",
              },
              "&:last-child td": {
                borderBottom: 0,
              },
            }}
          >
            {/* MANTENEDORA */}
            <TableCell
              sx={{
                px: 1.4,
                py: 0.35,
                borderRight: "1px solid var(--sear-borda, #e5edf5)",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.2,
                  minWidth: 0,
                }}
              >
                <Box
                  sx={{
                    width: 30,
                    height: 30,
                    borderRadius: "50%",
                    backgroundColor: "var(--sear-superficie-azul, #e7f1fd)",
                    display: "grid",
                    placeItems: "center",
                    flexShrink: 0,
                  }}
                >
                  <AccountBalanceRoundedIcon
                    sx={{
                      color: "var(--sear-azul, #087cf0)",
                      fontSize: 19,
                    }}
                  />
                </Box>

                  <ButtonBase
                    type="button"
                    disableRipple
                    onClick={() => onProfileClick(maintainer.id)}
                    sx={{
                      display: "block",
                      textAlign: "left",
                      width: "fit-content",
                      maxWidth: "100%",
                      userSelect: "text",
                      borderRadius: "3px",
                      lineHeight: "inherit",
                      "&.Mui-focusVisible": { outline: "2px solid var(--sear-borda-forte, #087cf0)", outlineOffset: 2 },
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      color: coresTextosGlobais.link,
                      fontSize: tamanhosPaginasAdministrativas.conteudoTabela,
                      fontWeight: 600,
                    }}
                  >
                    {maintainer.nome}
                  </ButtonBase>
              </Box>
            </TableCell>

            {/* CIDADE */}
            <TableCell
              sx={{
                px: 1.4,
                py: 0.35,
                borderRight: "1px solid var(--sear-borda, #e5edf5)",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  minWidth: 0,
                }}
              >
                <LocationOnIcon
                  sx={{
                    color: "var(--sear-azul, #087cf0)",
                    fontSize: iconesGlobais.conteudoTabela.fontSize,
                    flexShrink: 0,
                  }}
                />

                  <Typography
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      color: coresTextosGlobais.conteudoTabela,
                      fontSize: tamanhosPaginasAdministrativas.conteudoTabela,
                    }}
                  >
                    {maintainer.cidade || "Não informado"}
                  </Typography>
              </Box>
            </TableCell>

            {/* E-MAIL */}
            <TableCell
              sx={{
                px: 1.4,
                py: 0.35,
                borderRight: "1px solid var(--sear-borda, #e5edf5)",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  minWidth: 0,
                }}
              >
                <EmailIcon
                  sx={{
                    color: "var(--sear-azul, #087cf0)",
                    fontSize: iconesGlobais.conteudoTabela.fontSize,
                    flexShrink: 0,
                  }}
                />

                <Tooltip title={maintainer.email || ""}>
                  <Typography
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      color: coresTextosGlobais.conteudoTabela,
                      fontSize: tamanhosPaginasAdministrativas.conteudoTabela,
                    }}
                  >
                    {maintainer.email || "Não informado"}
                  </Typography>
                </Tooltip>
              </Box>
            </TableCell>

            {/* AÇÕES */}
            <TableCell
              align="center"
              sx={{
                px: 0.7,
                py: 0.35,
              }}
            >
              <Button
                variant="contained"
                size="small"
                onClick={() => onProfileClick(maintainer.id)}
                sx={{
                  minHeight: 30,
                  px: 1.6,
                  borderRadius: 1,
                  textTransform: "none",
                  fontSize: tamanhosControlesGlobais.botao,
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                  backgroundColor: "var(--sear-acao-azul, #087cf0)",
                  "&:hover": {
                    backgroundColor: "var(--sear-acao-azul-hover, #0767d8)",
                  },
                }}
              >
                Ver perfil
              </Button>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

// ======================================================
// PÁGINA DE MANTENEDORAS
// ======================================================

export default function Mantenedoras() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [searchQuery, setSearchQuery] = useState(
    searchParams.get("busca") || "",
  );

  const [selectedCity, setSelectedCity] = useState(
    searchParams.get("cidade") || "",
  );

  const [cityMenuAnchor, setCityMenuAnchor] = useState(null);

  const cityMenuOpen = Boolean(cityMenuAnchor);

  const [currentPage, setCurrentPage] = useState(() => {
    const savedPage = Number(searchParams.get("pagina"));

    return Number.isInteger(savedPage) && savedPage > 0 ? savedPage : 1;
  });

  const [itensPorPagina, setItensPorPagina] = useState(5);

  const [ordem, setOrdem] = useState({
    coluna: "nome",
    direcao: "asc",
  });

  const [mantenedoras, setMantenedoras] = useState([]);

  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  // ====================================================
  // CONSULTA DE MANTENEDORAS
  // ====================================================

  useEffect(() => {
    const fetchMantenedoras = async () => {
      try {
        const response = await fetch(api.allMaintainers(), {
          credentials: "include",
        });

        const data = await response.json();

        setMantenedoras(data.filter((mantenedora) => mantenedora.ativo));
      } catch (error) {
        console.error("Erro ao buscar os dados:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchMantenedoras();
  }, []);

  // ====================================================
  // PARÂMETROS DA URL
  // ====================================================

  useEffect(() => {
    const params = new URLSearchParams();

    const normalizedSearchQuery = searchQuery.trim();

    if (normalizedSearchQuery) {
      params.set("busca", normalizedSearchQuery);
    }

    if (selectedCity) {
      params.set("cidade", selectedCity);
    }

    if (currentPage > 1) {
      params.set("pagina", String(currentPage));
    }

    setSearchParams(params, {
      replace: true,
    });
  }, [searchQuery, selectedCity, currentPage, setSearchParams]);

  // ====================================================
  // ORDENAÇÃO
  // ====================================================

  const sortedMantenedoras = [...mantenedoras].sort((primeira, segunda) => {
    const resultado = compareText(
      normalizeText(primeira[ordem.coluna]),
      normalizeText(segunda[ordem.coluna]),
    );

    return ordem.direcao === "asc" ? resultado : -resultado;
  });

  // ====================================================
  // CIDADES DISPONÍVEIS
  // ====================================================

  const availableCities = Array.from(
    new Map(
      sortedMantenedoras
        .map((mantenedora) => mantenedora.cidade?.trim())
        .filter(Boolean)
        .map((city) => [normalizeText(city), city]),
    ).values(),
  ).sort(compareText);

  const normalizedSearchQuery = normalizeText(searchQuery);

  const normalizedSelectedCity = normalizeText(selectedCity);

  // ====================================================
  // BUSCA E FILTROS
  // ====================================================

  const hasExactCity =
    normalizedSearchQuery.length >= 2 &&
    sortedMantenedoras.some(
      (mantenedora) =>
        normalizeText(mantenedora.cidade) === normalizedSearchQuery,
    );

  const hasCityStartingWithTerm =
    normalizedSearchQuery.length >= 2 &&
    sortedMantenedoras.some((mantenedora) =>
      normalizeText(mantenedora.cidade).startsWith(normalizedSearchQuery),
    );

  const filteredMantenedoras = sortedMantenedoras.filter((mantenedora) => {
    const maintainerName = normalizeText(mantenedora.nome);

    const maintainerCity = normalizeText(mantenedora.cidade);

    if (normalizedSelectedCity && maintainerCity !== normalizedSelectedCity) {
      return false;
    }

    if (hasExactCity) {
      return maintainerCity === normalizedSearchQuery;
    }

    if (hasCityStartingWithTerm) {
      return maintainerCity.startsWith(normalizedSearchQuery);
    }

    return maintainerName.includes(normalizedSearchQuery);
  });

  // ====================================================
  // PAGINAÇÃO
  // ====================================================

  const totalPages = Math.max(
    1,
    Math.ceil(filteredMantenedoras.length / itensPorPagina),
  );

  const paginaSegura = Math.min(currentPage, totalPages);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const inicio = (paginaSegura - 1) * itensPorPagina;

  const fim = Math.min(inicio + itensPorPagina, filteredMantenedoras.length);

  const paginatedMantenedoras = filteredMantenedoras.slice(inicio, fim);

  const resumoResultados = filteredMantenedoras.length
    ? `Mostrando ${inicio + 1} a ${fim} de ${filteredMantenedoras.length} resultados`
    : "Mostrando 0 resultados";

  // ====================================================
  // EVENTOS
  // ====================================================

  const handleProfileClick = (id) => {
    navigate(`/perfil-mantenedora/${id}`);
  };

  const handleSort = (coluna) => {
    setOrdem((atual) => ({
      coluna,
      direcao:
        atual.coluna === coluna && atual.direcao === "asc" ? "desc" : "asc",
    }));

    setCurrentPage(1);
  };

  const handleCitySelect = (city) => {
    setSelectedCity(city);
    setCurrentPage(1);
    setCityMenuAnchor(null);
  };

  // ====================================================
  // CARREGAMENTO
  // ====================================================

  if (loading) {
    return (
      <Box sx={{ width: "100%", py: 0 }}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="80vh"
        >
          <motion.div
            animate={{ rotate: 360 }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: "linear",
            }}
          >
            <CircularProgress size={60} />
          </motion.div>
        </Box>
      </Box>
    );
  }

  // ====================================================
  // INTERFACE
  // ====================================================

  return (
    <Box sx={{ width: "100%", py: 0 }}>
      {/* CABEÇALHO */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.4,
          mb: 1,
        }}
      >
        <Box
          sx={{
            width: 44,
            height: 44,
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
            borderRadius: 1.5,
            color: "var(--sear-azul, #087cf0)",
            backgroundColor: "var(--sear-superficie-azul, #e8f4ff)",
          }}
        >
          <AccountBalanceRoundedIcon sx={{ fontSize: 27 }} />
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Typography
            component="h1"
            sx={{
              color: coresTextosGlobais.tituloPagina,
              fontSize: tamanhosPaginasAdministrativas.tituloPagina,
              fontWeight: 700,
              lineHeight: 1.15,
            }}
          >
            Mantenedoras Cadastradas
          </Typography>

          <Typography
            sx={{
              mt: 0.3,
              color: "var(--sear-texto-secundario, #7b8798)",
              fontSize: tamanhosControlesGlobais.descricao,
              lineHeight: 1.35,
            }}
          >
            Gerencie as mantenedoras cadastradas e consulte seus dados
            institucionais.
          </Typography>
        </Box>
      </Box>

      {/* BARRA DE PESQUISA E FILTROS */}
      <Box
        sx={{
          display: "flex",
          alignItems: {
            xs: "stretch",
            md: "center",
          },
          flexDirection: {
            xs: "column",
            md: "row",
          },
          gap: 1.25,
          px: 1.25,
          py: 0.55,
          mb: 0.5,
          border: "1px solid var(--sear-borda, #d5e7f7)",
          borderRadius: 1.5,
          backgroundColor: "var(--sear-superficie-azul, #f7fbff)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: {
              xs: "stretch",
              sm: "center",
            },
            flexDirection: {
              xs: "column",
              sm: "row",
            },
            flex: 1,
            minWidth: 0,
            gap: 1.25,
          }}
        >
          {/* PESQUISA */}
          <TextField
            size="small"
            variant="outlined"
            placeholder="Buscar mantenedora ou cidade..."
            inputProps={{
              "aria-label": "Buscar mantenedora ou cidade",
            }}
            value={searchQuery}
            onChange={(event) => {
              setSearchQuery(event.target.value);
              setCurrentPage(1);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    sx={{
                      color: "var(--sear-azul, #087cf0)",
                      fontSize: 18,
                    }}
                  />
                </InputAdornment>
              ),
            }}
            sx={{
              width: {
                xs: "100%",
                sm: 440,
              },
              maxWidth: "100%",
              "& .MuiOutlinedInput-root": {
                height: 34,
                backgroundColor: "var(--sear-superficie, #ffffff)",
                borderRadius: 1,
                "& fieldset": {
                  borderColor: "var(--sear-borda, #d7e0ea)",
                },
                "&:hover fieldset": {
                  borderColor: "var(--sear-borda, #a9c9e8)",
                },
              },
              "& .MuiInputBase-input": {
                fontSize: tamanhosControlesGlobais.campoBusca,
              },
            }}
          />

          {/* FILTRO POR CIDADE */}
          <Button
            id="city-filter-button"
            size="small"
            variant={selectedCity ? "contained" : "outlined"}
            startIcon={<LocationOnIcon />}
            endIcon={<KeyboardArrowDownIcon />}
            onClick={(event) => setCityMenuAnchor(event.currentTarget)}
            aria-controls={cityMenuOpen ? "city-filter-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={cityMenuOpen ? "true" : undefined}
            sx={{
              height: 34,
              minWidth: 120,
              borderRadius: 1,
              textTransform: "none",
              fontSize: tamanhosControlesGlobais.botao,
              fontWeight: 500,
              whiteSpace: "nowrap",
            }}
          >
            {selectedCity || "Todas as Cidades"}
          </Button>

          <Menu
            id="city-filter-menu"
            anchorEl={cityMenuAnchor}
            open={cityMenuOpen}
            onClose={() => setCityMenuAnchor(null)}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            MenuListProps={{
              "aria-labelledby": "city-filter-button",
            }}
            PaperProps={{
              sx: {
                mt: 1,
                minWidth: 240,
                maxHeight: 320,
                borderRadius: "10px",
              },
            }}
          >
            <MenuItem
              selected={!selectedCity}
              onClick={() => handleCitySelect("")}
              sx={{
                display: "flex",
                gap: 2,
              }}
            >
              <Box component="span" sx={{ flex: 1 }}>
                Todas as cidades
              </Box>

              {!selectedCity && <CheckIcon color="primary" fontSize="small" />}
            </MenuItem>

            {availableCities.map((city) => {
              const isSelected = normalizedSelectedCity === normalizeText(city);

              return (
                <MenuItem
                  key={normalizeText(city)}
                  selected={isSelected}
                  onClick={() => handleCitySelect(city)}
                  sx={{
                    display: "flex",
                    gap: 2,
                  }}
                >
                  <Box component="span" sx={{ flex: 1 }}>
                    {city}
                  </Box>

                  {isSelected && <CheckIcon color="primary" fontSize="small" />}
                </MenuItem>
              );
            })}
          </Menu>
        </Box>

        {/* SEPARADOR */}
        <Box
          sx={{
            display: {
              xs: "none",
              md: "block",
            },
            width: "1px",
            height: 30,
            flexShrink: 0,
            backgroundColor: "var(--sear-superficie-azul, #d8e7f4)",
          }}
        />

        {/* REGISTROS POR PÁGINA */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: {
              xs: "space-between",
              md: "flex-end",
            },
            gap: 1.2,
            flexShrink: 0,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: "var(--sear-texto-secundario, #667b98)",
              fontSize: tamanhosControlesGlobais.rotulo,
              whiteSpace: "nowrap",
            }}
          >
            Registros por página:
          </Typography>

          <Select
            size="small"
            value={itensPorPagina}
            onChange={(event) => {
              setItensPorPagina(Number(event.target.value));
              setCurrentPage(1);
            }}
            renderValue={(valor) => `${valor} por página`}
            inputProps={{
              "aria-label": "Registros por página",
            }}
            sx={{
              width: 160,
              height: 34,
              backgroundColor: "var(--sear-superficie, #ffffff)",
              color: "var(--sear-azul, #314d76)",
              borderRadius: 1,
              fontSize: tamanhosControlesGlobais.seletor,
              fontWeight: 500,
              "& .MuiSelect-select": {
                py: 0.55,
              },
            }}
          >
            {OPCOES_ITENS_POR_PAGINA.map((quantidade) => (
              <MenuItem key={quantidade} value={quantidade}>
                {quantidade} por página
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Box>

      {/* PAGINAÇÃO SUPERIOR */}
      {filteredMantenedoras.length > 0 && (
        <Box
          sx={{
            display: "flex",
            alignItems: {
              xs: "flex-start",
              sm: "center",
            },
            justifyContent: "space-between",
            flexDirection: {
              xs: "column",
              sm: "row",
            },
            gap: 0.75,
            px: 1.5,
            py: 0.35,
            mb: 0.5,
            border: "1px solid var(--sear-borda, #d7e8f8)",
            borderRadius: 1.5,
            backgroundColor: "var(--sear-superficie-azul, #f2f8fe)",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: "var(--sear-texto-secundario, #536b8b)",
              fontSize: tamanhosControlesGlobais.contador,
            }}
          >
            {filteredMantenedoras.length}{" "}
            {filteredMantenedoras.length === 1 ? "registro" : "registros"}
          </Typography>

          <Box
            sx={{
              ml: {
                sm: "auto",
              },
            }}
          >
            <PaginacaoLista
              paginaAtual={paginaSegura}
              totalPaginas={totalPages}
              onChange={setCurrentPage}
              ariaLabel="Paginação superior da lista de mantenedoras"
            />
          </Box>
        </Box>
      )}

      {/* TABELA OU ESTADO VAZIO */}
      {filteredMantenedoras.length === 0 ? (
        <Fade in timeout={500}>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            minHeight="50vh"
            textAlign="center"
          >
            <motion.div
              animate={{
                scale: [1, 1.1, 1],
                opacity: [0.8, 1, 0.8],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
              }}
            >
              <InfoIcon
                color="action"
                sx={{
                  fontSize: 60,
                  mb: 2,
                }}
              />
            </motion.div>

            <Typography variant="h5" gutterBottom>
              Nenhuma mantenedora encontrada
            </Typography>

            <Typography variant="body1" color="textSecondary">
              {searchQuery
                ? "Tente ajustar sua busca"
                : "Não há mantenedoras cadastradas"}
            </Typography>
          </Box>
        </Fade>
      ) : (
        <MaintainerTable
          maintainers={paginatedMantenedoras}
          onProfileClick={handleProfileClick}
          ordem={ordem}
          onSort={handleSort}
        />
      )}

      {/* PAGINAÇÃO INFERIOR */}
      {filteredMantenedoras.length > 0 && (
        <Box
          sx={{
            display: "flex",
            alignItems: {
              xs: "flex-start",
              sm: "center",
            },
            justifyContent: "space-between",
            flexDirection: {
              xs: "column",
              sm: "row",
            },
            gap: 0.75,
            mt: 0.45,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: "var(--sear-texto-secundario, #526b8d)",
              fontSize: tamanhosControlesGlobais.contador,
            }}
          >
            {resumoResultados}
          </Typography>

          <Box
            sx={{
              ml: {
                sm: "auto",
              },
            }}
          >
            <PaginacaoLista
              paginaAtual={paginaSegura}
              totalPaginas={totalPages}
              onChange={setCurrentPage}
              ariaLabel="Paginação inferior da lista de mantenedoras"
            />
          </Box>
        </Box>
      )}
    </Box>
  );
}
